import './AppDownload.less';

import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import { fromJS } from 'immutable';
import React, { useEffect, useState } from 'react';
import { dispatch, useReframe } from 're-framejs';

import { motionOpacityVariant } from 'nike-ui/src/common/util';
import Markdown from 'nike-ui/src/components/markdown/Markdown';
import i18n from '@i18n';
import { getConfigProperty, getDevice, getUrl } from '@/common/oldMobile';
import { fireGAEvent } from '@/analytics';
import BlockLoader from '@/ui/BlockLoader';
import NikeLogoIcon from '@/icons/NikeLogoIcon';
import useScrollTop from '@/_hooks/useScrollTop';
import { catcher } from '@/_http/utils';
import { Button } from 'nike-ui/src/components/button/Button';
import { CONTACT_US } from '@/routing/routes';
import { navigate } from '@/routing';
import { scrollBody } from '@/common/client';

function GalleryImages() {
    const images = [];
    const staticUrl = getConfigProperty('static.url');
    ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10'].forEach(i =>
        // eslint-disable-next-line jsx-a11y/alt-text
        images.push(<img src={staticUrl + `/nike/cms_uploads/nan_screen_${i}.jpg`} key={i} />)
    );
    return images;
}

function Gallery({ overlay, setOverlay }) {
    useScrollTop();

    return (
        <AnimatePresence>
            {overlay && (
                <motion.div
                    className="app-gallery-shadow"
                    key="backdrop"
                    variants={motionOpacityVariant}
                    initial="initial"
                    animate="enter"
                    exit="exit"
                    onClick={() => {
                        scrollBody(true);
                        setOverlay(false);
                    }}
                >
                    <div className="images-wrap">
                        <GalleryImages />
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function getNikeApp() {
    const device = getDevice();
    const appleUrl = getConfigProperty('get.app.ios');
    fireGAEvent('Nativna aplikacia', 'Download');
    if (device === 'android') {
        window.location.href = getConfigProperty('get.app.android') + '?a=' + new Date().getTime();
    } else {
        window.open(appleUrl, '_blank');
    }
}

function AppDownload() {
    useEffect(() => {
        axios
            .get(getUrl('APP_DOWNLOAD'))
            .then(data => dispatch(['appDownload.loaded', fromJS(data)]))
            .catch(catcher.handleMasterError);
    }, []);
    const [overlay, setOverlay] = useState(true);
    const [galleryOpen, setGalleryOpen] = useState(false);
    const derefSub = useReframe();
    const texts = derefSub(['app_download.texts']);

    if (!texts || !texts.get('text1')) {
        return <BlockLoader />;
    }

    return (
        <div className="app-download">
            <div className="app-banner">
                <div className="logo">
                    <NikeLogoIcon width={70} height={48} opacity={1} fill={'#ffffff'} />
                </div>
                <div className="banner-box">
                    <h1>{i18n('application.banner.text')}</h1>
                    <h3>{i18n('application.banner.subtext')}</h3>
                </div>
            </div>
            <div className="app-content">
                <div className="app-block">
                    <div className="app-info-box">
                        <div className="app-logo">
                            <NikeLogoIcon width={45} height={23} opacity={1} fill={'#ffffff'} />
                        </div>
                        <div className="app-info">
                            <h1 className="name">{i18n('application.smart.banner.name')}</h1>
                            <h2 className="downloads">{i18n('application.smart.banner.company')}</h2>
                        </div>
                        <h3 className="age">{i18n('application.smart.banner.age')}</h3>
                    </div>
                    <Button onClick={getNikeApp} label={i18n('app.download')} />
                </div>
                <div className="grey-bg">
                    <div className="app-block gallery">
                        <h3>{i18n('app.download.gallery')}</h3>
                        <div
                            className="app-gallery"
                            onClick={() => {
                                scrollBody(false);
                                setOverlay(true);
                                setGalleryOpen(true);
                            }}
                        >
                            <GalleryImages />
                        </div>
                        {galleryOpen && (
                            <>
                                <div
                                    className="app-gallery-delete"
                                    onClick={() => {
                                        scrollBody(true);
                                        setOverlay(false);
                                        setTimeout(() => {
                                            setGalleryOpen(false);
                                        }, 200);
                                    }}
                                >
                                    &times;
                                </div>
                                <Gallery overlay={overlay} setOverlay={setOverlay} />
                            </>
                        )}
                    </div>
                    <div className="separator" />
                    <div className="app-block">
                        <Help text={texts.get('text1')} />
                        <div className="app-support">
                            <a onClick={() => navigate(CONTACT_US)}>{i18n('app.download.support')}</a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AppDownload;

function Help({ text }) {
    const overrides = {
        ol: {
            component: p => (
                <div>
                    {React.Children.map(p.children, (li, i) => {
                        return (
                            <div className="row">
                                <div className="point">
                                    <div className="circle">
                                        <div>{i + 1}</div>
                                    </div>
                                </div>
                                <div className="description">{li.props.children}</div>
                            </div>
                        );
                    })}
                </div>
            )
        }
    };
    return <Markdown overrides={overrides}>{text}</Markdown>;
}
